import { CardHeader, Panel, ProgressBar } from '@bcecloud/portal-ui';
import { compactMoney } from '@/lib/sales';
import type { CoveragePeriod } from '@/lib/sales-analytics';

/** Industry rule of thumb (mirrors the API's COVERAGE_TARGET): $3 of pipeline per $1 still to book. */
const COVERAGE_TARGET = 3;

export interface SalesCoveragePanelProps {
  readonly coverage: readonly CoveragePeriod[];
  /** All open deals now, whatever their close date. */
  readonly open: { readonly deals: number; readonly pipeline: number };
}

const shortDay = (ymd: string): string =>
  new Date(`${ymd}T00:00:00.000Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

function CoverageColumn({ period }: { readonly period: CoveragePeriod }): React.ReactElement {
  const covered = period.ratio === null || period.ratio >= COVERAGE_TARGET;
  return (
    <div className="min-w-0">
      <p className="text-portal-micro text-portal-muted">
        {period.label} · {period.daysLeft} days left
      </p>
      <p className="mt-1 text-portal-card-title font-bold text-portal-ink tabular-nums">
        {period.goalRemaining > 0 ? `${compactMoney(period.goalRemaining)} to go` : 'Goal met'}
      </p>
      <ProgressBar
        className="mt-3"
        label={`${period.label} booked against the goal`}
        value={period.booked}
        max={period.goal}
        caption={`${compactMoney(period.booked)} of ${compactMoney(period.goal)} goal`}
      />
      <p className="mt-3 text-portal-small text-portal-ink">
        <span className={covered ? 'font-bold text-portal-success' : 'font-bold text-portal-danger'}>
          {period.ratio === null ? 'Covered' : `${period.ratio.toFixed(1)}× coverage`}
        </span>{' '}
        · {compactMoney(period.pipeline)} in {period.deals.toLocaleString('en-US')} open deals due by {shortDay(period.to)} (target{' '}
        {COVERAGE_TARGET}×)
      </p>
      {period.goalRemaining > 0 ? (
        <p className="mt-1 text-portal-small text-portal-muted">Needs {compactMoney(period.neededPerDay)} booked a day to finish on goal</p>
      ) : null}
    </div>
  );
}

/**
 * Pipeline coverage: is there enough open pipeline, due to close in time, to
 * book what is left of the $10M-a-year goal this quarter and this year?
 * Always as of today.
 */
export function SalesCoveragePanel({ coverage, open }: SalesCoveragePanelProps): React.ReactElement {
  return (
    <Panel className="p-5">
      <CardHeader
        title="Pipeline coverage"
        description={`Open deals due to close between today and the period's end against the goal still to book (past-due deals are left out as stale). ${open.deals.toLocaleString('en-US')} open deals worth ${compactMoney(open.pipeline)} in all. As of today.`}
      />
      <div className="grid gap-6 md:grid-cols-2">
        {coverage.map((period) => (
          <CoverageColumn key={period.key} period={period} />
        ))}
      </div>
    </Panel>
  );
}
